/**
 * The client's reading of whether an impersonation swap took effect.
 *
 * A leaf module with no imports, so the Playwright spec asserting these
 * messages can import them rather than restate them. What this split buys is
 * not dragging React, the Nexus barrel and the tRPC client into
 * the test process to read two strings. No spec imports through the `@/` alias,
 * so whether Playwright resolves it here is untested and not relied on.
 */

/** The two fields of `user.me` that say who the server is answering as. */
export interface EffectiveIdentity {
  id: string
  impersonatingFrom: string | null
}

/**
 * What the verification read established.
 *
 * Three outcomes, not two. Collapsing `unknown` into `refused` is the mistake
 * this type exists to prevent: the read failing says nothing about whether the
 * cookies stuck, and the swap may well be live.
 *
 * On `applied` and `refused` the answer lands in the shared `user.me` cache, so
 * `ImpersonationBanner` renders the truth in place. On `unknown` there IS no
 * answer to write, so the cache keeps the pre-swap identity and the banner
 * stays hidden — which is why that message tells the admin to reload rather
 * than describing what happened. A mitigation, not a reconciliation; do not
 * read this type as promising the latter in all three cases.
 */
export type SwapVerdict = 'applied' | 'refused' | 'unknown'

/**
 * What the admin is told when the browser demonstrably kept no usable pair.
 *
 * Only issued for `refused` — the server answered, and its answer says this
 * browser is not impersonating the target.
 */
export const IMPERSONATION_REFUSED_MESSAGE =
  'Impersonation did not take effect — this browser is not carrying the session. Check that cookies are allowed for this site.'

/**
 * What the admin is told when the verification read produced no usable answer.
 *
 * **It names no cause, and that is the point.** This one branch is reached by a
 * dead socket, a gateway 502, a 429 from the catch-all limiter, and an expired
 * session — the server answered in three of those four. "Your browser rejected
 * the session cookies" and "we could not reach the server" are both false for
 * most of the branch, and the second is false in the very case the test drives.
 * The rule in `.agents/reference/client-network-state.md` is to name what you
 * know, and what is known here is only that the switch could not be confirmed.
 *
 * The swap may be LIVE — `impersonation.start` had already answered 200 with
 * its `Set-Cookie` pair before this read was issued — so the instruction is to
 * reload, which is what makes the true state visible.
 */
export const IMPERSONATION_UNVERIFIED_MESSAGE =
  'Could not confirm the switch. Reload to see who you are signed in as.'

/**
 * Did the swap the server agreed to actually take effect for this browser?
 *
 * Read from a re-fetched `user.me`, which is the only observable: the cookie
 * pair is `httpOnly`, so nothing on the page can inspect it directly.
 *
 * Both conjuncts are load-bearing and they fail differently:
 *
 * - `impersonatingFrom` unset is the ORDINARY answer for a browser that kept
 *   no pair — the case the whole check exists for.
 * - a mismatched `id` is a pair that WAS honoured and resolved to somebody
 *   else. Its reachable producer is two starts racing — two tabs, or a
 *   double-click on a surface with no pending guard — where both requests are
 *   issued before either `Set-Cookie` lands, so neither is refused by the
 *   server, and whichever response arrives LAST wins the jar. (A stale pair
 *   from an EARLIER impersonation cannot produce it. Two different guards
 *   refuse that, depending on the target: normally `ctx.userRole` is the
 *   target's while a pair is honoured, so `adminProcedure` throws FORBIDDEN;
 *   where the target is themselves a superadmin the role gate passes and
 *   `impersonationService.start` throws `Already impersonating`. Either way no
 *   cookie is written.) Navigating on a mismatch would put the admin into an
 *   account they did not choose.
 */
export function impersonationApplied(
  me: EffectiveIdentity | null | undefined,
  targetUserId: string,
): boolean {
  if (!me) return false
  return me.impersonatingFrom != null && me.id === targetUserId
}

/**
 * What the admin is told when the browser demonstrably still carries the pair.
 *
 * The MIRROR of `IMPERSONATION_REFUSED_MESSAGE`, and its own string rather than
 * a reuse, because the two states are opposite and the instruction differs:
 * there, nothing happened and they are still themselves; here, the switch they
 * asked to leave is still live, and every request they make is still being
 * answered as the customer.
 */
export const IMPERSONATION_STOP_REFUSED_MESSAGE =
  'Still viewing as this customer — the browser did not clear the impersonation. Check that cookies are allowed for this site.'

/**
 * What the admin is told when the verification read produced no usable answer.
 *
 * **It names no cause**, for the reason `IMPERSONATION_UNVERIFIED_MESSAGE`
 * gives at length: this one branch is a dead socket, a 502, a 429 and an
 * expired session alike.
 *
 * The stop may well have WORKED — `impersonation.stop` deletes the pair before
 * it does anything else, and had already answered 200 with its clearing
 * `Set-Cookie` header before this read was issued — so the instruction is to
 * reload, which is what makes the true state visible.
 */
export const IMPERSONATION_STOP_UNVERIFIED_MESSAGE =
  'Could not confirm you have stopped. Reload to see who you are signed in as.'

/**
 * Did the stop the server agreed to actually take effect for this browser?
 *
 * `impersonation.stop` clears the pair as a side effect of its 200, so the
 * mutation resolving proves the server ISSUED the clear and never that the
 * browser APPLIED it — the same argument `impersonationApplied` makes about
 * `start`, one direction over.
 *
 * The consequence of getting it wrong is worse than for `start`, and asymmetric
 * with it. A start that silently failed leaves the admin as themselves, which
 * is safe. A stop that silently failed lands them on `/ops/users` still
 * resolved as the customer — who holds no `access_users`, so the surface 403s —
 * while the banner still renders and its Stop button 200s again without
 * changing anything, because with the pair honoured the service never throws.
 * The pair is `httpOnly`, so the page cannot drop it either. It rides for the
 * full 24h `maxAge`.
 *
 * **Only `impersonatingFrom` is read, and the id deliberately is not.** For
 * `start` a mismatched id is a real outcome — two starts racing, the last
 * `Set-Cookie` winning the jar. There is no such shape here: with the pair
 * gone the server answers as whoever the session cookie names, and comparing
 * that to an id the client remembers would refuse a perfectly good stop after
 * an unrelated session change.
 */
export function impersonationCleared(
  me: EffectiveIdentity | null | undefined,
): boolean {
  if (!me) return false
  return me.impersonatingFrom == null
}
